/* eslint-disable complexity */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, TabLayout } from 'src/@vymo/ui/blocks';
import SkeletonLoader from 'src/@vymo/ui/blocks/skelton';
import { Filters } from 'src/@vymo/ui/molecules';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import { Keys, locale } from 'src/i18n';
import { useFilters } from 'src/models/filters/hooks';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  getAppSearchParams,
  getModuleProps,
  isLmsWeb,
} from 'src/workspace/utils';
import EmptyState from '../components/emptyState';
import ErrorState from '../components/errorState';
import {
  getKeyMetricsFilters,
  getKeyMetricsGroups,
  getKeyMetricsList,
  getStatus,
} from '../selector';
import { fetchKeyMetrics } from '../thunk';
import { fetchLeadKeyMetrics } from '../widget/thunk';
import { getGroupedMetrics, normalizeFilterPayloadDates } from './queries';
import KeyMetricsTable from './table';
import styles from './index.module.scss';

function KeyMetrics() {
  const dispatch = useAppDispatch();
  const keyMetrics = useAppSelector(getKeyMetricsList);
  const filterFields = useAppSelector(getKeyMetricsFilters);
  const keyMetricsGroups = useAppSelector(getKeyMetricsGroups);
  const status = useAppSelector((state) => getStatus(state, 'key_metrics'));

  const groupedMetrics = useMemo(
    () => getGroupedMetrics(keyMetrics, keyMetricsGroups),
    [keyMetrics, keyMetricsGroups],
  );

  const [selectedGroupKey, setSelectedGroupKey] = useState<string>('');

  const selectedGroup = useMemo(
    () =>
      groupedMetrics?.find((group) => group?.key === selectedGroupKey) ||
      groupedMetrics?.[0],
    [groupedMetrics, selectedGroupKey],
  );

  const metaData = useMemo(() => {
    const allUsers = getModuleProps?.()?.getAllUsers?.();
    const hierarchy = getModuleProps?.()?.getUserHierarchy?.();
    const usersData = getModuleProps?.()?.getUsersList?.() || {};
    return {
      allUsers,
      userHierarchy: hierarchy || {},
      users: usersData,
    };
  }, []);

  const getKeyMetrics = (filterPayload = {}) => {
    const normalizedFilterPayload = normalizeFilterPayloadDates(filterPayload);
    const params = getAppSearchParams();
    if (params?.source === 'vo') {
      dispatch(
        fetchLeadKeyMetrics({ params, filters: normalizedFilterPayload }),
      );
    } else {
      dispatch(fetchKeyMetrics({ filters: normalizedFilterPayload }));
    }
  };

  const { filters, filterCount, handleApply, handleReset } = useFilters({
    context: 'key_metrics',
    filterFields: filterFields || [],
    metaData,
    formVersion: FormVersion.webRevyre,
    onApply: (filterPayload) => {
      getKeyMetrics(filterPayload);
    },
    onReset: () => {
      getKeyMetrics();
    },
  });

  useEffect(() => {
    if (!groupedMetrics?.length) {
      setSelectedGroupKey('');
      return;
    }

    if (!groupedMetrics.some((group) => group?.key === selectedGroupKey)) {
      setSelectedGroupKey(groupedMetrics[0]?.key || '');
    }
  }, [groupedMetrics, selectedGroupKey]);

  useEffect(() => {
    getKeyMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabItems = useMemo(
    () =>
      groupedMetrics
        ?.filter((group) => group?.cards?.length)
        .map((group) => ({
          key: group.key,
          label: group.label,
        })),
    [groupedMetrics],
  );

  const renderGroupedContent = () => (
    <>
      <TabLayout
        items={tabItems}
        defaultKey={selectedGroup?.key || ''}
        onChange={(key) => setSelectedGroupKey(key)}
      />
      <KeyMetricsTable
        cards={selectedGroup?.cards || []}
        key={selectedGroup.key}
        filters={filters}
      />
    </>
  );

  const renderUngroupedContent = () => (
    <KeyMetricsTable cards={keyMetrics} filters={filters} />
  );

  return (
    <div>
      <Filters
        filterFields={filterFields || []}
        metaData={metaData}
        formVersion={FormVersion.web}
        filters={filters}
        filterCount={filterCount}
        onApply={handleApply}
        onReset={handleReset}
        buttonClassname={isLmsWeb() ? `${styles.lmsWebFilters}` : ''}
      />
      <Card classNames={styles.wrapper}>
        {Boolean(status === 'loading' || !status) && (
          <SkeletonLoader height={300} rect />
        )}
        {Boolean(
          status === 'done' && groupedMetrics?.length > 0 && keyMetrics?.length,
        ) && renderGroupedContent()}
        {Boolean(
          status === 'done' &&
            keyMetrics?.length > 0 &&
            keyMetricsGroups?.length === 0,
        ) && renderUngroupedContent()}
        {Boolean(status === 'done' && !keyMetrics?.length) && (
          <EmptyState
            message={locale(Keys.NO_DATA_TO_SHOW, {
              tab: 'key metrics',
            })}
          />
        )}
        {status === 'error' && <ErrorState />}
      </Card>
    </div>
  );
}

export default KeyMetrics;
